import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Boxes, Clock, Layers, Sparkles, Flame, Gift, Megaphone } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { NeonBackground } from "../components/NeonBackground";
import { Cube3D } from "../components/Cube3D";
import { InstallPrompt } from "../components/InstallPrompt";
import { Button } from "../components/ui/button";
import { api, fileUrl } from "../lib/api";

const HERO = "https://images.unsplash.com/photo-1702863361902-93c51bfbd923?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHwzRCUyMHByaW50ZXIlMjBuZW9uJTIwZ2xvd3xlbnwwfHx8fDE3ODU2NTU0MzJ8MA&ixlib=rb-4.1.0&q=85";

const features = [
  { icon: Layers, title: "Stampa 3D", desc: "Dal prototipo al pezzo in serie: PLA, PETG, resina e finiture su misura.", color: "#00F0FF" },
  { icon: Flame, title: "Incisione Laser", desc: "Incisioni precise su legno, acrilico, pelle e metallo rivestito.", color: "#FF007F" },
  { icon: Clock, title: "Tracciamento Live", desc: "Segui lo stato del tuo lavoro: in attesa, in lavorazione, completato, consegnato.", color: "#39FF14" },
];

export default function Landing() {
  const [texts, setTexts] = useState({});
  const [promos, setPromos] = useState([]);
  const [locandine, setLocandine] = useState([]);
  useEffect(() => {
    const today = new Date().toLocaleDateString("en-CA", { timeZone: "Europe/Rome" });
    api.get("/settings").then((r) => {
      setTexts(r.data.texts || {});
      setPromos((r.data.promos || []).filter((p) => p.active !== false && (!p.expires_at || String(p.expires_at).slice(0, 10) >= today)));
    }).catch(() => {});
    api.get("/promotions").then((r) => setLocandine((r.data || []).filter((p) => p.show_on_home && (!p.expires_at || String(p.expires_at).slice(0, 10) >= today)))).catch(() => {});
  }, []);
  return (
    <div className="noise-overlay min-h-screen">
      <NeonBackground />
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="" aria-hidden className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/85 to-background/50" />
        </div>
        <Cube3D className="hidden lg:block absolute top-28 right-[9%] opacity-70 z-10" size={104} variant="cyan" />
        <Cube3D className="hidden lg:block absolute bottom-20 left-[7%] opacity-60 z-10" size={72} variant="pink" />
        <div className="relative z-20 max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-28">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }} className="flex justify-center mb-4">
            <img src="/logo-web.png" alt="AC Creator 3D" data-testid="hero-logo" className="w-[280px] sm:w-[380px] lg:w-[440px] h-auto animate-logo" />
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }} className="max-w-3xl mx-auto text-center">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 mb-6 text-xs font-mono uppercase tracking-widest text-neon-cyan border border-neon-cyan/40 rounded-sm bg-neon-cyan/5">
              <Sparkles className="w-3.5 h-3.5" /> Stampa 3D & Incisione Laser
            </span>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
              <span className="neon-text">{texts.hero_title || "Dai vita alle tue idee"}</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl mx-auto font-light">
              {texts.hero_subtitle || "Invia la tua richiesta, calcola un preventivo istantaneo e segui lo stato del lavoro in tempo reale. Stampa 3D e incisioni laser di precisione."}
            </p>
            <div className="mt-10 flex flex-wrap gap-4 justify-center">
              <Link to="/richiesta">
                <Button data-testid="hero-request-btn" className="h-12 px-7 bg-neon-cyan text-black hover:bg-white rounded-sm font-semibold text-base gap-2 transition-colors duration-200 group animate-glow-breathe">
                  Invia una richiesta
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                </Button>
              </Link>
              <Link to="/preventivo">
                <Button data-testid="hero-quote-btn" variant="outline" className="h-12 px-7 border-white/20 text-white hover:border-neon-green hover:text-neon-green hover:bg-transparent rounded-sm font-semibold text-base transition-colors duration-200">
                  Calcola preventivo
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Promozioni dinamiche */}
      {promos.length > 0 && (
        <section data-testid="promo-banners" className="max-w-7xl mx-auto px-5 sm:px-8 pt-10 space-y-3">
          {promos.map((p, i) => (
            <motion.div key={p.id || i} data-testid={`promo-banner-${i}`}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
              className="relative overflow-hidden p-5 sm:p-6 rounded-sm border border-neon-yellow/40 bg-card flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
              <div className="flex items-start gap-4 min-w-0">
                <div className="w-11 h-11 rounded-sm flex items-center justify-center border border-neon-yellow/50 bg-neon-yellow/10 shrink-0">
                  <Sparkles className="w-5 h-5" style={{ color: "#FFE600" }} />
                </div>
                <div className="min-w-0">
                  {p.title && <h3 className="font-display font-black text-lg sm:text-xl tracking-tight text-white">{p.title}</h3>}
                  {p.text && <p className="text-muted-foreground font-light mt-1 text-sm">{p.text}</p>}
                </div>
              </div>
              {p.link_url && (
                <a href={p.link_url} target={p.link_url.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
                  data-testid={`promo-banner-link-${i}`}
                  className="shrink-0 inline-flex items-center gap-2 h-10 px-5 rounded-sm font-semibold text-black bg-neon-yellow hover:bg-white transition-colors duration-200" style={{ backgroundColor: "#FFE600" }}>
                  {p.link_label || "Scopri di più"} <ArrowRight className="w-4 h-4" />
                </a>
              )}
            </motion.div>
          ))}
        </section>
      )}

      {/* Locandine promozionali */}
      {locandine.length > 0 && (
        <section data-testid="home-locandine" className="max-w-7xl mx-auto px-5 sm:px-8 pt-10">
          <div className="flex items-end justify-between mb-5">
            <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight text-white flex items-center gap-2"><Megaphone className="w-6 h-6 text-neon-pink" /> Promozioni</h2>
            <Link to="/promozioni" data-testid="home-locandine-all" className="text-sm text-neon-cyan hover:underline inline-flex items-center gap-1">Vedi tutte <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {locandine.slice(0, 4).map((p, i) => (
              <Link to="/promozioni" key={p.id} data-testid={`home-locandina-${i}`}
                className="group relative aspect-[4/5] rounded-sm overflow-hidden border border-white/10 hover:border-neon-pink/50 transition-colors duration-200">
                <img src={fileUrl(p.image_path)} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              data-testid={`feature-card-${i}`}
              className="group relative p-8 bg-card border border-white/10 rounded-sm hover:-translate-y-1 transition-transform duration-300"
              style={{ boxShadow: "none" }}
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-sm mb-6 border" style={{ borderColor: `${f.color}55`, backgroundColor: `${f.color}12` }}>
                <f.icon className="w-6 h-6" style={{ color: f.color }} />
              </div>
              <h3 className="font-display font-bold text-xl tracking-tight text-white mb-2">{f.title}</h3>
              <p className="text-sm text-muted-foreground font-light leading-relaxed">{f.desc}</p>
              <div className="absolute inset-0 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                   style={{ boxShadow: `0 0 24px ${f.color}33`, border: `1px solid ${f.color}44` }} />
            </motion.div>
          ))}
        </div>
      </section>

      {/* Loyalty promo */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-6">
        <div data-testid="loyalty-promo" className="relative overflow-hidden p-8 sm:p-10 rounded-sm border border-neon-green/40 bg-card flex flex-col sm:flex-row items-center gap-6 justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-sm flex items-center justify-center border border-neon-green/50 bg-neon-green/10 shrink-0">
              <Gift className="w-7 h-7 text-neon-green" />
            </div>
            <div>
              <h3 className="font-display font-black text-2xl tracking-tight text-white">Programma Fedeltà</h3>
              <p className="text-muted-foreground font-light mt-1">Al tuo <b className="text-neon-green">10° ordine</b> ricevi automaticamente uno <b className="text-neon-green">sconto del 10%</b>. Usa sempre la stessa email!</p>
            </div>
          </div>
          <Link to="/preventivo"><Button className="bg-neon-green text-black hover:bg-white rounded-sm font-semibold whitespace-nowrap transition-colors duration-200">Verifica il tuo sconto</Button></Link>
        </div>
      </section>

      {/* Install app (PWA) */}
      <InstallPrompt />

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-28">
        <div className="relative overflow-hidden p-12 sm:p-16 bg-card border border-neon-pink/30 rounded-sm text-center neon-grid-bg">
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter text-white">
            Pronto a stampare il tuo progetto?
          </h2>
          <p className="mt-4 text-muted-foreground font-light max-w-lg mx-auto">
            Descrivi la tua idea, carica un'immagine di riferimento e ti risponderemo al più presto.
          </p>
          <Link to="/richiesta">
            <Button data-testid="cta-request-btn" className="mt-8 h-12 px-8 bg-neon-pink text-white hover:bg-white hover:text-black rounded-sm font-semibold gap-2 transition-colors duration-200">
              Inizia ora <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-white/10 py-8 text-center">
        <p className="font-mono text-xs text-muted-foreground">AC CREATOR 3D — Stampa 3D & Incisione Laser · {new Date().getFullYear()}</p>
      </footer>
    </div>
  );
}
